import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import LogoSlot from "./LogoSlot";
import { navLinks } from "../data/content";
import { LOGO_FULL_SRC } from "../constants/brand";

// In-page sections the desktop nav can highlight while scrolling (home page only).
const sectionIds = ["platforms", "technology", "culture"];

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeHash, setActiveHash] = useState("");

  useEffect(() => {
    const sections = sectionIds
      .map((id) => document.getElementById(id))
      .filter(Boolean);

    if (sections.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveHash(`#${entry.target.id}`);
        });
      },
      { rootMargin: "-50% 0px -40% 0px" }
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <header className="sticky top-0 z-50 h-14 border-b border-site-hairline bg-white/80 backdrop-blur-md">
        <div className="mx-auto flex h-14 max-w-site items-center justify-between px-6">
          <a href="/" className="flex items-center" aria-label="Omnyx home">
            <LogoSlot
              src={LOGO_FULL_SRC}
              alt="Omnyx"
              className="h-[30px] w-auto"
              placeholderClassName="h-[30px] w-[30px] text-site-ink"
            />
          </a>

          {/* Desktop nav */}
          <nav aria-label="Primary" className="hidden md:block">
            <ul className="flex items-center gap-2">
              {navLinks.map((link) => {
                if (link.external) {
                  return (
                    <li key={link.name}>
                      <a
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 rounded-full bg-omnyx-teal/10 px-3 py-1.5 text-sm font-semibold text-omnyx-teal transition-colors hover:bg-omnyx-teal/15 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-omnyx-teal"
                      >
                        {link.name}
                      </a>
                    </li>
                  );
                }

                const isActive = activeHash && link.href.endsWith(activeHash);
                return (
                  <li key={link.name}>
                    <a
                      href={link.href}
                      className={`rounded px-3 py-1.5 text-sm font-medium transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-omnyx-teal ${
                        isActive
                          ? "text-omnyx-teal"
                          : "text-site-ink/80 hover:text-site-ink"
                      }`}
                    >
                      {link.name}
                    </a>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Mobile toggle */}
          <button
            type="button"
            className="rounded text-site-ink md:hidden focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-omnyx-teal"
            onClick={() => setIsMenuOpen((open) => !open)}
            aria-expanded={isMenuOpen}
            aria-controls="mobile-nav"
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile nav */}
        {isMenuOpen && (
          <nav
            id="mobile-nav"
            aria-label="Mobile"
            className="border-b border-site-hairline bg-white/95 backdrop-blur-md md:hidden"
          >
            <ul className="space-y-1 px-6 py-4">
              {navLinks.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    target={link.external ? "_blank" : undefined}
                    rel={link.external ? "noopener noreferrer" : undefined}
                    onClick={() => setIsMenuOpen(false)}
                    className={`block rounded py-2 text-base font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-omnyx-teal ${
                      link.external ? "text-omnyx-teal" : "text-site-ink/80"
                    }`}
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        )}
      </header>
    </>
  );
}
